import { Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { Response, Http, RequestOptionsArgs, URLSearchParams, Headers } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { environment } from 'environments/environment';
import { LoaderService } from './loader.service';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/finally';

/**
 * Representa los parametros de una URL
 */
interface Parameters {
  [key: string]: any;
}

/**
 * Representa un error de comunicacion con el backend
 */
export interface BackendError {
  codigo: number;
  mensaje: string;
}

export interface BackendResult<T> {
  data: T;
  errors: any[];
  status: any;
}

@Injectable()
export class SxHttpService {

  /**
   * URL base del backend.
   */
  private baseUrl: string = environment.apiSOC;

  private errorsSubject = new ReplaySubject<BackendError[]>();
  public errors = this.errorsSubject as Observable<BackendError[]>;
  private refreshTokenKey: string = "soc_refreshToken";

  /**
   * @param http Http provider.
   * @param jwtService Nuestro servicio de JWT.
   */
  constructor(private http: Http,
    private router: Router,
    public loaderService: LoaderService) { }

  /**
   * Retorna un recurso del backend
   *
   * @param url La URL relativa al recurso.
   * @param parameters Parametros para añadir a la url, o null.
   */
  getFromAbsolute(url: string, parameters?: Parameters): Observable<Response> {
    this.loaderService.mostrarLoader();

    return this.http.get(url, this.createOptions(parameters))
      .catch((err, caugth) => this.manejarError(err, caugth))
      .finally(() => {
        this.loaderService.ocultarLoader();
      });
  }

  /**
   * Get resource from backend
   * @param url
   * @param parameters
   * @param showLoader
   */
  get(url: string, parameters?: Parameters, showLoader?: boolean): Observable<Response> {
    const hideLoader = showLoader != null && !showLoader;

    if (!hideLoader) {
      this.loaderService.mostrarLoader();
    }

    return this.http.get(this.baseUrl + url, this.createOptions(parameters))
      .catch((err, caugth) => this.manejarError(err, caugth))
      .finally(() => {
        if (!hideLoader) {
          this.loaderService.ocultarLoader();
        }
      });
  }

  /**
   * Actualiza un recurso en el backend
   *
   * @param url La URL relativa al recurso.
   * @param body El recurso actualizado.
   * @param parameters Parametros para añadir a la url, o null.
   */
  put(url: string, body: any, parameters?: Parameters): Observable<Response> {
    this.loaderService.mostrarLoader();
    return this.http.put(this.baseUrl + url, body, this.createOptions(parameters))
      .catch((err, caugth) => this.manejarError(err, caugth))
      .finally(() => {
        this.loaderService.ocultarLoader();
      });
  }
  postFromAbsolute(url: string, body: any, parameters?: Parameters, headers?: Parameters): Observable<Response> {
    this.loaderService.mostrarLoader();
    return this.http.post(url, body)
      .catch((err, caugth) => this.manejarError(err, caugth))
      .finally(() => {
        this.loaderService.ocultarLoader();
      });
  }
  /**
   * Crea un recurso en el backend
   *
   * @param url La URL relativa al recurso.
   * @param body El recurso a crear.
   * @param parameters Parametros para añadir a la url, o null.
   * @param headers Headers para añadir a la url, o null.
   */
  post(url: string, body: any, parameters?: Parameters, headers?: Parameters): Observable<Response> {
    this.loaderService.mostrarLoader();
    return this.http.post(this.baseUrl + url, body, this.createOptions(parameters, headers))
      .catch((err, caugth) => this.manejarError(err, caugth))
      .finally(() => {
        this.loaderService.ocultarLoader();
      });
  }

  /**
   * Elimina un recurso en el backend
   *
   * @param url La URL relativa al recurso.
   * @param parameters Parametros para añadir a la url, o null.
   */
  delete(url: string, parameters?: Parameters): Observable<Response> {
    this.loaderService.mostrarLoader();
    return this.http.delete(this.baseUrl + url, this.createOptions(parameters))
      .catch((err, caugth) => this.manejarError(err, caugth))
      .finally(() => {
        this.loaderService.ocultarLoader();
      });
  }

  /**
   * Mergea las opciones pasadas con las opciones necesarias para interactuar con el
   * servicio de backend
   *
   * @param parameters Parametros para añadir a la url, o null.
   * @param headers Headers para agregar al request, o null.
   * @returns RequestOptionsArgs
   */
  private createOptions(parameters?: Parameters, headers?: Parameters): RequestOptionsArgs {
    const result = {
      params: new URLSearchParams(),
      headers: new Headers(),
      withCredentials : true
    };

    // Agrega los paramatros
    if (parameters != null) {
      for (const key in parameters) {
        if (parameters.hasOwnProperty(key)) {
          const value = parameters[key];
          if (value != null) {
            if ( Array.isArray(value) ) {
              for (let index = 0; index < value.length; index++) {
                result.params.append(key, value[index]);
              }
            } else {
              result.params.set(key, value);
            }
            //  result.params.append("code", "Prod");
          }
        }
      }
    }

    // Agrega los headers
    if (headers != null) {
      for (const key in headers) {
        if (headers.hasOwnProperty(key)) {
          const value = headers[key];
          if (value != null) {
            result.headers.append(key, value.toString());
          }
        }
      }
    }

    return result;
  }

  /**
   * Maneja los errores en las comunicaciones con el Backend
   *
   * @param error La respuesta HTTP del backend, o cualquier error que ocurra.
   * @returns {any}
   */
   private manejarError(error: Response | any, caugth: Observable<Response>) {
    // Loggin habilitado si estamos en Dev Mode
    if (isDevMode()) {
      console.error(error);
    }

    if (error instanceof Response) { // El error proviene del backend
      if(error.status == 401 && localStorage.getItem(this.refreshTokenKey))
      {
        return this.refreshJwt().flatMap(response =>
        {
          let refreshed = response.json().data as boolean;
          if(refreshed)            
            return caugth;
          else
          {
            localStorage.removeItem(this.refreshTokenKey);

            // Descomprimimos la respuesta y la arrojamos
            const respuesta = (<Response>error);
            const resultado = this.descomprimirError(respuesta);
            this.errorsSubject.next(resultado);
            return Observable.throw(resultado);
          }
        });        
      }
      else
      {
        // Descomprimimos la respuesta y la arrojamos
        const respuesta = (<Response>error);
        const resultado = this.descomprimirError(respuesta);
        this.errorsSubject.next(resultado);
        return Observable.throw(resultado);
      }
    } else {
      // cualquier otro caso, construimos el error
      const result = <BackendError[]>[{
        codigo: null,
        mensaje: error.toString()
      }];

      this.errorsSubject.next(result);
      return Observable.throw(result);
    }
  }

  /**
   * Descomprime una respuesta para transformarla en un {@link BackendError}.
   */
  // tslint:disable-next-line:member-ordering
  private descomprimirError(respuesta: Response): BackendError[] {
    switch (respuesta.status) {
      case 0:
        return <BackendError[]>([{
          codigo: 0,
          mensaje: 'SERVIDOR_NO_RESPONDE',
        }]);
      case 400:
      case 403:
      case 409:
      case 500: {
        const data = respuesta.json();
        if (data.exceptionMessage) {
          return <BackendError[]>([{
            codigo: 500,
            mensaje: data.exceptionMessage,
          }]);
        }
        if (data.message && data.messageDetail) {
          return <BackendError[]>([{
            codigo: respuesta.status,
            mensaje: data.messageDetail,
          }]);
        }
        return <BackendError[]>(respuesta.text() ? SxHttpService.castErrorMessage(data) : { mensaje: respuesta.statusText, codigo: respuesta.status });
      }
      case 401: {
        this.router.navigate(['/login']);
        return null;
      }
      case 404: {
        this.router.navigateByUrl('/soc/404');
        return <BackendError[]>([{
          codigo: 404,
          mensaje: 'Resource does not exist',
        }]);
      }
      default: {
        const data = respuesta.json();
        return <BackendError[]>([{
          codigo: data.errors[0].errorCode,
          mensaje: data.errors[0].errorMessage || 'Error inesperado'
        }]);
      }
    }
  }

  // tslint:disable-next-line:member-ordering
  private static castErrorMessage(data: any): BackendError[] {
    const erroresBackend: BackendError[] = [];
    if (data.errors && data.errors.length > 0) {
      data.errors.forEach(element => {
        const errorItem = <BackendError>{
          codigo: element.errorCode,
          mensaje: element.errorMessage
        };
        erroresBackend.push(errorItem);
      });
    }
    return erroresBackend;
  }

  refreshJwt() {
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    const params = new URLSearchParams();
    params.append(this.refreshTokenKey, localStorage.getItem(this.refreshTokenKey));
    const body = params.toString();

    return this.post('api/Authentication/RefreshJwt', body, {}, headers);
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/infrastructure/services/sxHttp.service.ts